// Admin: the shop-floor scanners that run the scanner app with no login.
//
// A scanner is enrolled once, by typing a short code into the kiosk app. The code
// is shown here a single time, so the drawer that shows it is the only place it
// exists. Revoking cuts the device off within about a minute (the API caches the
// revoked flag for 60 s).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import DrawerShell from '../Receiving/DrawerShell';
import { DataTable } from '../components/table/DataTable';
import type { EnrollmentCode, ScannerDevice } from './scannerDeviceTypes';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import Tag, { type TagTone } from '../components/ui/Tag';
import EmptyState from '../components/ui/EmptyState';
import Skeleton from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';


type Status = 'active' | 'revoked' | 'waiting';

const statusOf = (d: ScannerDevice): Status =>
  d.revoked_at ? 'revoked' : d.enrolled_at ? 'active' : 'waiting';

// A word in a tag: the word carries the state, the tone only reinforces it.
const STATUS_VIEW: Record<Status, { label: string; tone: TagTone }> = {
  active: { label: 'Active', tone: 'good' },
  revoked: { label: 'Revoked', tone: 'bad' },
  waiting: { label: 'Waiting for code', tone: 'neutral' },
};

const when = (iso: string | null) => (iso ? dayjs(iso).format('MMM D, YYYY h:mm A') : '—');

interface TableMeta {
  onRevoke: (d: ScannerDevice) => void;
  busyId: string | null;
}

/** How a scanner is called in copy: its ID, which is also what is on its sticker. */
const scannerLabel = (d: ScannerDevice) => d.scanner_id ?? d.name ?? 'this scanner';

// Module scope per the DataTable contract: stable identity, state arrives on meta.
const COLUMNS: ColumnDef<ScannerDevice, any>[] = [
  {
    id: 'scanner_id',
    header: 'ID',
    meta: { cellClass: 'font-mono font-medium text-on-surface tabular-nums' },
    cell: ({ row }) => row.original.scanner_id || '—',
  },
  {
    id: 'serial',
    header: 'Serial',
    meta: { cellClass: 'whitespace-nowrap font-mono text-on-surface-variant tabular-nums' },
    cell: ({ row }) => row.original.hardware_serial || '—',
  },
  {
    id: 'mac',
    header: 'Wi-Fi MAC',
    meta: { cellClass: 'whitespace-nowrap font-mono text-on-surface-variant' },
    cell: ({ row }) => row.original.wifi_mac || '—',
  },
  {
    id: 'name',
    header: 'Label',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.name || '—',
  },
  {
    id: 'enrolled',
    header: 'Enrolled',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant tabular-nums' },
    cell: ({ row }) => when(row.original.enrolled_at),
  },
  {
    id: 'by',
    header: 'Enrolled by',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => row.original.enrolled_by_name || '—',
  },
  {
    id: 'last',
    header: 'Last seen',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant tabular-nums' },
    cell: ({ row }) => when(row.original.last_token_at),
  },
  {
    id: 'status',
    header: 'Status',
    meta: { cellClass: 'whitespace-nowrap' },
    cell: ({ row }) => {
      const v = STATUS_VIEW[statusOf(row.original)];
      return <Tag tone={v.tone}>{v.label}</Tag>;
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: (info) => {
      const { onRevoke, busyId } = info.table.options.meta as TableMeta;
      const d = info.row.original;
      if (d.revoked_at) return null;
      return (
        <button
          type="button"
          aria-label={`Revoke ${scannerLabel(d)}`}
          disabled={busyId === d.id}
          onClick={() => onRevoke(d)}
          className={buttonClass('danger', 'sm')}
        >
          Revoke
        </button>
      );
    },
  },
];

const LoadingBars: React.FC = () => (
  <div className="px-4 py-8 space-y-3">
    {['w-48', 'w-32', 'w-40'].map((width) => (
      <Skeleton key={width} className={`h-3 rounded ${width}`} />
    ))}
  </div>
);

const formatRemaining = (ms: number) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// The code is eight characters; a gap in the middle is for reading it aloud, and
// the copy button hands over the unspaced string.
const spaced = (code: string) => `${code.slice(0, 4)} ${code.slice(4)}`;

const AddScannerDrawer: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const api = useApi();
  const toast = useToast();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState<EnrollmentCode | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!issued) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [issued]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    setSaving(true);
    try {
      const res = (await api.post('/scanner-devices/codes', { body: { name: trimmed } })) as EnrollmentCode;
      setIssued(res);
      setNow(Date.now());
      onCreated();
    } catch (err: any) {
      toast.error(err?.message || 'Could not make a code. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.code);
      setCopied(true);
    } catch {
      toast.error('Copy is blocked here. Select the code and copy it by hand.');
    }
  };

  const remaining = issued ? new Date(issued.expires_at).getTime() - now : 0;
  const expired = !!issued && remaining <= 0;

  return (
    <DrawerShell title="Add scanner" onClose={onClose} dirty={!issued && name.trim() !== ''}>
      <FadeIn key={issued ? 'code' : 'form'}>
      {!issued ? (
        <form onSubmit={create} className="space-y-4">
          <div>
            <label htmlFor="scanner-name" className="mb-1 block text-xs font-medium text-on-surface-variant">
              Label (optional)
            </label>
            <Field
              thumb
              id="scanner-name"
              data-drawer-autofocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoComplete="off"
              placeholder="Bench 2 handheld"
            />
            <p className="mt-1.5 text-xs text-on-surface-variant">
              Where it lives, if that helps. The scanner is known by the last four of its serial number,
              which it reports when it enrolls.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={buttonClass('quiet')}>Cancel</button>
            <button type="submit" disabled={saving} className={buttonClass('filled')}>
              {saving ? 'Making code...' : 'Get code'}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Type this code on the scanner's setup screen. It is shown once.
          </p>
          <div
            className={`rounded-md bg-surface-container-highest px-4 py-6 text-center transition-opacity duration-300 motion-reduce:transition-none ${expired ? 'opacity-40' : ''}`}
          >
            <div
              aria-label={`Enrollment code ${issued.code.split('').join(' ')}`}
              className="select-all font-mono text-4xl sm:text-5xl font-bold tracking-[0.15em] text-primary tabular-nums"
            >
              {spaced(issued.code)}
            </div>
          </div>
          <p role="status" className="text-sm text-on-surface-variant tabular-nums">
            {expired
              ? 'This code expired. Close this and add the scanner again for a new one.'
              : `Expires in ${formatRemaining(remaining)} (30 minutes from when it was made).`}
          </p>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={copy} disabled={expired} className={buttonClass('quiet')}>
              {copied ? 'Copied' : 'Copy code'}
            </button>
            <button type="button" onClick={onClose} className={buttonClass('filled')}>Done</button>
          </div>
        </div>
      )}
      </FadeIn>
    </DrawerShell>
  );
};

const ScannerDevices: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<ScannerDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const load = async () => {
    try {
      const data = (await api.get('/scanner-devices')) as ScannerDevice[];
      if (mounted.current) setRows(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Could not load scanners. Reload to try again.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revoke = async (d: ScannerDevice) => {
    const ok = await confirm({
      title: `Revoke ${scannerLabel(d)}?`,
      message: 'This scanner stops working within a minute and cannot be switched back on. To use the handheld again, add it as a new scanner.',
      confirmLabel: 'Revoke scanner',
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await api.post(`/scanner-devices/${d.id}/revoke`);
      toast.success(`${scannerLabel(d)} revoked.`);
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Could not revoke. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const meta = useMemo(() => ({ onRevoke: revoke, busyId }), [busyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageLayout kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Scanners"
        lede="Shop-floor handhelds that run the scanner app without a login."
        help={
          <div className="space-y-3 text-sm">
            <p>Add a scanner to get an 8-character code. Type it into the scanner app once and that handheld is enrolled.</p>
            <p>Operators still badge in and enter a PIN. Everything they do is stamped with their name, not the handheld's.</p>
            <p>Revoke a scanner that is lost or retired. It stops working within a minute.</p>
          </div>
        }
        primary={<PrimaryAction onClick={() => setAdding(true)}>Add scanner</PrimaryAction>}
      />

      <DataTable<ScannerDevice>
        data={rows}
        columns={COLUMNS}
        getRowId={(r) => r.id}
        meta={meta}
        rowProps={(r) => ({ className: r.revoked_at ? 'opacity-60' : '' })}
        loading={loading}
        loadingContent={<LoadingBars />}
        empty={
          <EmptyState
            variant="area"
            title="No scanners enrolled yet."
            action={(
              <button type="button" onClick={() => setAdding(true)} className={buttonClass('filled')}>
                Add your first scanner
              </button>
            )}
          >
            Add one to get a code, then type the code into the scanner app on the handheld.
          </EmptyState>
        }
      />

      {adding && (
        <AddScannerDrawer
          onClose={() => setAdding(false)}
          onCreated={() => { load(); }}
        />
      )}
    </PageLayout>
  );
};

export default ScannerDevices;
